import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Textarea } from '@/shared/components/ui/textarea';
import { toast } from 'sonner';
import {
  FileText, Plus, RefreshCw, Search, Eye, Send, MoreHorizontal,
  DollarSign, Calendar, Building2, CheckCircle, CreditCard, Trash2 } from
'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from
'@/shared/components/ui/dropdown-menu';

const API_URL = API_BASE;
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('ezerpToken')}`
});

const statusColors = {
  draft: 'bg-gray-500/20 text-gray-400',
  submitted: 'bg-blue-500/20 text-blue-400',
  in_review: 'bg-cyan-500/20 text-cyan-400',
  approved: 'bg-green-500/20 text-green-400',
  posted: 'bg-emerald-500/20 text-emerald-400',
  partial: 'bg-amber-500/20 text-amber-400',
  paid: 'bg-purple-500/20 text-purple-400',
  voided: 'bg-red-500/20 text-red-400'
};

export const APBillsPage = () => {const { t } = useTranslation("finance");
  const [bills, setBills] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showDetails, setShowDetails] = useState(null);
  const [formData, setFormData] = useState({
    vendor_id: '',
    vendor_invoice_no: '',
    bill_type: 'standard',
    bill_date: new Date().toISOString().split('T')[0],
    due_date: '',
    currency: 'USD',
    notes: '',
    lines: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 0 }]
  });

  const fetchBills = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/finance/ap/bills?`;
      if (statusFilter !== 'all') url += `status=${statusFilter}&`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch bills');
      const data = await res.json();
      setBills(Array.isArray(data) ? data : data.bills || []);
    } catch (err) {
      toast.error(err.message);
      setBills([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await fetch(`${API_URL}/api/finance/ap/vendors`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setVendors(Array.isArray(data) ? data : data.vendors || []);
      }
    } catch (err) {console.error(err);}
  };

  useEffect(() => {fetchBills();fetchVendors();}, [statusFilter]);
  useEffect(() => {const timer = setTimeout(fetchBills, 300);return () => clearTimeout(timer);}, [search]);

  const handleCreate = async () => {
    try {
      const payload = {
        ...formData,
        vendor_id: parseInt(formData.vendor_id),
        lines: formData.lines.filter((l) => l.description).map((l) => ({
          ...l,
          quantity: parseFloat(l.quantity) || 1,
          unit_price: parseFloat(l.unit_price) || 0,
          tax_rate: parseFloat(l.tax_rate) || 0
        }))
      };
      const res = await fetch(`${API_URL}/api/finance/ap/bills`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to create bill');
      toast.success('Bill created');
      setShowCreate(false);
      setFormData({
        vendor_id: '', vendor_invoice_no: '', bill_type: 'standard',
        bill_date: new Date().toISOString().split('T')[0],
        due_date: '', currency: 'USD', notes: '',
        lines: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 0 }]
      });
      fetchBills();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleApprove = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/finance/ap/bills/${id}/approve`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to approve bill');
      toast.success('Bill approved');
      fetchBills();
    } catch (err) {toast.error(err.message);}
  };

  const handlePost = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/finance/ap/bills/${id}/post`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to post bill');
      toast.success('Bill posted');
      fetchBills();
    } catch (err) {toast.error(err.message);}
  };

  const handleVoid = async (id) => {
    if (!window.confirm('Are you sure you want to void this bill?')) return;
    try {
      const res = await fetch(`${API_URL}/api/finance/ap/bills/${id}/void`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to void bill');
      toast.success('Bill voided');
      fetchBills();
    } catch (err) {toast.error(err.message);}
  };

  const viewDetails = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/finance/ap/bills/${id}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch details');
      setShowDetails(await res.json());
    } catch (err) {toast.error(err.message);}
  };

  const addLine = () => {
    setFormData((prev) => ({
      ...prev,
      lines: [...prev.lines, { description: '', quantity: 1, unit_price: 0, tax_rate: 0 }]
    }));
  };

  const updateLine = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      lines: prev.lines.map((line, i) => i === index ? { ...line, [field]: value } : line)
    }));
  };

  const subtotal = formData.lines.reduce((sum, l) => sum + (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0), 0);
  const totalOutstanding = bills.reduce((sum, b) => sum + (b.amount_due || 0), 0);
  const overdueCount = bills.filter((b) => b.status === 'posted' && new Date(b.due_date) < new Date()).length;

  return (
    <div className="space-y-6" data-testid="ap-bills-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("apBills", "AP Bills")}</h1>
          <p className="text-muted-foreground mt-1">{t("manageAccountsPayable", "Manage accounts payable bills")}</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="gap-2 bg-green-600 hover:bg-green-700">
          <Plus className="h-4 w-4" />{t("newBill", "New Bill")}
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("totalBills", "Total Bills")}</p>
                <p className="text-3xl font-bold">{bills.length}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-500/20"><FileText className="h-6 w-6 text-blue-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("outstanding", "Outstanding")}</p>
                <p className="text-3xl font-bold">${totalOutstanding.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-lg bg-red-500/20"><DollarSign className="h-6 w-6 text-red-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("overdue", "Overdue")}</p>
                <p className="text-3xl font-bold">{overdueCount}</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-500/20"><Calendar className="h-6 w-6 text-amber-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("vendors", "Vendors")}</p>
                <p className="text-3xl font-bold">{vendors.length}</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-500/20"><Building2 className="h-6 w-6 text-purple-500" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search bills..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allStatus", "All Status")}</SelectItem>
                <SelectItem value="draft">{t("draft", "Draft")}</SelectItem>
                <SelectItem value="submitted">{t("submitted", "Submitted")}</SelectItem>
                <SelectItem value="approved">{t("approved", "Approved")}</SelectItem>
                <SelectItem value="posted">{t("posted", "Posted")}</SelectItem>
                <SelectItem value="paid">{t("paid", "Paid")}</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchBills}><RefreshCw className="h-4 w-4" /></Button>
          </div>

          {loading ?
          <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500" />
            </div> :
          bills.length === 0 ?
          <div className="text-center py-12">
              <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg text-muted-foreground">{t("noBillsFound", "No bills found")}</p>
            </div> :

          <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left p-3 font-medium">{t("bill", "Bill #")}</th>
                    <th className="text-left p-3 font-medium">{t("vendor", "Vendor")}</th>
                    <th className="text-left p-3 font-medium">{t("billDate", "Bill Date")}</th>
                    <th className="text-left p-3 font-medium">{t("dueDate", "Due Date")}</th>
                    <th className="text-left p-3 font-medium">{t("total", "Total")}</th>
                    <th className="text-left p-3 font-medium">{t("status", "Status")}</th>
                    <th className="text-right p-3 font-medium">{t("actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {bills.map((bill) =>
                <tr key={bill.id} className="hover:bg-slate-800/30">
                      <td className="p-3 font-medium">{bill.bill_no}</td>
                      <td className="p-3">{bill.vendor_name || `Vendor #${bill.vendor_id}`}</td>
                      <td className="p-3">{bill.bill_date}</td>
                      <td className="p-3">{bill.due_date}</td>
                      <td className="p-3 font-medium">${(bill.total || 0).toLocaleString()}</td>
                      <td className="p-3"><Badge className={statusColors[bill.status]}>{bill.status}</Badge></td>
                      <td className="p-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => viewDetails(bill.id)}>
                              <Eye className="h-4 w-4 mr-2" />{t("viewDetails", "View Details")}
                        </DropdownMenuItem>
                            {(bill.status === 'submitted' || bill.status === 'in_review') &&
                        <DropdownMenuItem onClick={() => handleApprove(bill.id)}>
                                <CheckCircle className="h-4 w-4 mr-2 text-green-500" />{t("approve", "Approve")}
                        </DropdownMenuItem>
                        }
                            {bill.status === 'approved' &&
                        <DropdownMenuItem onClick={() => handlePost(bill.id)}>
                                <Send className="h-4 w-4 mr-2 text-blue-500" />{t("post", "Post")}
                        </DropdownMenuItem>
                        }
                            {bill.status !== 'voided' && bill.status !== 'paid' &&
                        <DropdownMenuItem onClick={() => handleVoid(bill.id)} className="text-red-400">
                                <Trash2 className="h-4 w-4 mr-2" />{t("void", "Void")}
                        </DropdownMenuItem>
                        }
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                )}
                </tbody>
              </table>
            </div>
          }
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("newApBill", "New AP Bill")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("vendor", "Vendor *")}</Label>
                <Select value={formData.vendor_id} onValueChange={(v) => setFormData({ ...formData, vendor_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent>
                    {vendors.map((v) =>
                    <SelectItem key={v.id} value={String(v.id)}>{v.name}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("vendorInvoice", "Vendor Invoice #")}</Label>
                <Input value={formData.vendor_invoice_no} onChange={(e) => setFormData({ ...formData, vendor_invoice_no: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>{t("billDate", "Bill Date *")}</Label>
                <Input type="date" value={formData.bill_date} onChange={(e) => setFormData({ ...formData, bill_date: e.target.value })} />
              </div>
              <div>
                <Label>{t("dueDate", "Due Date *")}</Label>
                <Input type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} />
              </div>
              <div>
                <Label>{t("currency", "Currency")}</Label>
                <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">{t("usd", "USD")}</SelectItem>
                    <SelectItem value="EUR">{t("eur", "EUR")}</SelectItem>
                    <SelectItem value="GBP">{t("gbp", "GBP")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>{t("billLines", "Bill Lines")}</Label>
                <Button variant="outline" size="sm" onClick={addLine}><Plus className="h-4 w-4 mr-1" />{t("addLine", "Add Line")}</Button>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="text-left p-2">{t("description", "Description")}</th>
                      <th className="text-left p-2 w-20">{t("qty", "Qty")}</th>
                      <th className="text-left p-2 w-28">{t("unitPrice", "Unit Price")}</th>
                      <th className="text-left p-2 w-20">{t("tax", "Tax %")}</th>
                      <th className="text-left p-2 w-28">{t("amount", "Amount")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {formData.lines.map((line, idx) =>
                    <tr key={idx}>
                        <td className="p-2">
                          <Input value={line.description} onChange={(e) => updateLine(idx, 'description', e.target.value)} placeholder="Description" />
                        </td>
                        <td className="p-2">
                          <Input type="number" value={line.quantity} onChange={(e) => updateLine(idx, 'quantity', e.target.value)} />
                        </td>
                        <td className="p-2">
                          <Input type="number" value={line.unit_price} onChange={(e) => updateLine(idx, 'unit_price', e.target.value)} />
                        </td>
                        <td className="p-2">
                          <Input type="number" value={line.tax_rate} onChange={(e) => updateLine(idx, 'tax_rate', e.target.value)} />
                        </td>
                        <td className="p-2 font-medium">
                          ${((parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0)).toLocaleString()}
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="bg-slate-800/30">
                    <tr>
                      <td colSpan="4" className="p-2 text-right font-medium">{t("subtotal", "Subtotal:")}</td>
                      <td className="p-2 font-bold">${subtotal.toLocaleString()}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div>
              <Label>{t("notes", "Notes")}</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Bill notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("cancel", "Cancel")}</Button>
            <Button onClick={handleCreate} className="bg-green-600 hover:bg-green-700">{t("createBill", "Create Bill")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={!!showDetails} onOpenChange={() => setShowDetails(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{t("billDetails", "Bill Details")}</DialogTitle></DialogHeader>
          {showDetails &&
          <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground">{t("bill", "Bill #")}</p>
                  <p className="font-bold">{showDetails.bill_no}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground">{t("vendor", "Vendor")}</p>
                  <p className="font-medium">{showDetails.vendor_name}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <p className="text-sm text-muted-foreground">{t("status", "Status")}</p>
                  <Badge className={statusColors[showDetails.status]}>{showDetails.status}</Badge>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                  <p className="text-sm text-muted-foreground">{t("total", "Total")}</p>
                  <p className="text-2xl font-bold text-red-400">${(showDetails.total || 0).toLocaleString()}</p>
                </div>
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                  <p className="text-sm text-muted-foreground">{t("paid", "Paid")}</p>
                  <p className="text-2xl font-bold text-green-400">${(showDetails.amount_paid || 0).toLocaleString()}</p>
                </div>
                <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <p className="text-sm text-muted-foreground">{t("due", "Due")}</p>
                  <p className="text-2xl font-bold text-amber-400">${(showDetails.amount_due || 0).toLocaleString()}</p>
                </div>
              </div>
            </div>
          }
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetails(null)}>{t("close", "Close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};

export default APBillsPage;